const dataSources = [
  { name: "장면 전환 감지", desc: "컷 타이밍과 장면 구조를 프레임 단위로 자동 분석", icon: "ri-film-line", value: "96.3% 정확도", color: "#00D9FF" },
  { name: "음성 감정 분석", desc: "화자의 발화 강도, 감정 톤, 속도 변화 실시간 감지", icon: "ri-mic-line", value: "94.8% 정밀도", color: "#10B981" },
  { name: "표정·반응 감지", desc: "출연자 표정 변화와 스튜디오 반응 강도 자동 추출", icon: "ri-emotion-line", value: "91.2% 재현율", color: "#F59E0B" },
  { name: "주제 희소성 점수", desc: "콘텐츠 주제의 바이럴 가능성과 화제성 자동 측정", icon: "ri-fire-line", value: "TOP 5% 정밀", color: "#00D9FF" },
  { name: "화자 전환 분석", desc: "다중 화자 구분 및 발언 구조 자동 세그먼트화", icon: "ri-group-line", value: "8ms 해상도", color: "#10B981" },
  { name: "자막 자동 추출", desc: "방송 자막 OCR 및 음성 기반 자막 동기화 처리", icon: "ri-closed-captioning-line", value: "99.1% 정확", color: "#F59E0B" },
];

const platforms = [
  { name: "VOD 파일 업로드", icon: "ri-upload-cloud-line", color: "#00D9FF" },
  { name: "방송국 FTP 연동", icon: "ri-server-line", color: "#10B981" },
  { name: "클라우드 스토리지", icon: "ri-cloud-line", color: "#F59E0B" },
  { name: "제작사 API 연동", icon: "ri-code-line", color: "#00D9FF" },
  { name: "방송 파일 포맷", icon: "ri-film-line", color: "#10B981" },
  { name: "실시간 캡처", icon: "ri-broadcast-line", color: "#F59E0B" },
];

export default function DataIngestionSection() {
  return (
    <section id="features" className="py-28 bg-[#0A0E14] relative overflow-hidden">
      <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-[#00D9FF]/5 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-[#00D9FF] text-xs font-semibold uppercase tracking-widest mb-4 block" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            01 / 방송 콘텐츠 자동 분석
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            방송의 모든 순간을 정밀하게 읽습니다
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            케이블TV·종편·지상파 영상 파일을 업로드하면 AI가 장면, 음성, 표정, 주제를 동시에 분석하여
            숏폼으로 만들기 최적인 구간을 자동으로 찾아냅니다.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div className="bg-[#0F1419] border border-white/8 rounded-2xl p-8 relative overflow-hidden">
            <div className="absolute top-4 right-4">
              <span className="flex items-center gap-1.5 text-xs text-[#10B981]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse inline-block" />
                분석 파이프라인 처리 중
              </span>
            </div>
            <h3 className="text-white font-semibold text-lg mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              AI 분석 로그
            </h3>
            <div className="space-y-3 mb-8">
              {[
                { source: "scene.detector", msg: "장면 전환 감지 완료 → 총 87개 컷포인트 추출", type: "spike" },
                { source: "speech.emotion", msg: "발화 강도 급변 구간 감지 → t=38:14 (신뢰도 97%)", type: "growth" },
                { source: "face.analysis", msg: "표정 변화 강도: 최고치 | 감정: 충격·공감 | 지속: 4.2초", type: "sentiment" },
                { source: "topic.rarity", msg: "주제 희소성 점수: 9.7/10 | 바이럴 가능성: 상위 3%", type: "interaction" },
                { source: "subtitle.ocr", msg: "자막 42줄 추출 완료 | 음성 싱크 오차: 0.02초", type: "retention" },
              ].map((row, i) => (
                <div
                  key={i}
                  className="bg-[#1A1F2E]/60 rounded-lg px-4 py-3 flex items-start gap-3 border-l-2"
                  style={{
                    borderColor: row.type === "spike" ? "#00D9FF" : row.type === "growth" ? "#10B981" : row.type === "sentiment" ? "#F59E0B" : row.type === "interaction" ? "#00D9FF" : "#10B981",
                    fontFamily: "'JetBrains Mono', monospace",
                  }}
                >
                  <span className="text-gray-600 text-xs mt-0.5 shrink-0">[{row.source}]</span>
                  <span className="text-gray-300 text-xs leading-relaxed">{row.msg}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-white/8">
              {[
                { label: "분석 속도", value: "48배속" },
                { label: "평균 처리 시간", value: "2.8분" },
                { label: "지원 포맷", value: "30+" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-[#00D9FF] font-bold text-xl mb-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {stat.value}
                  </div>
                  <div className="text-gray-500 text-xs">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {dataSources.map((src) => (
                <div key={src.name} className="bg-[#0F1419] border border-white/8 rounded-xl p-5 hover:border-[#00D9FF]/20 transition-all duration-300 cursor-pointer group">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-9 h-9 flex items-center justify-center rounded-lg shrink-0" style={{ backgroundColor: `${src.color}15` }}>
                      <i className={`${src.icon} text-base`} style={{ color: src.color }} />
                    </div>
                    <div>
                      <div className="text-white text-sm font-semibold mb-0.5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{src.name}</div>
                      <div className="text-gray-500 text-xs leading-relaxed">{src.desc}</div>
                    </div>
                  </div>
                  <div className="text-right font-bold text-base" style={{ color: src.color, fontFamily: "'JetBrains Mono', monospace" }}>{src.value}</div>
                </div>
              ))}
            </div>
            <div className="bg-[#0F1419] border border-white/8 rounded-xl p-6">
              <div className="text-sm font-semibold text-gray-300 mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>파일 수신 방식</div>
              <div className="flex flex-wrap gap-3">
                {platforms.map((p) => (
                  <div key={p.name} className="flex items-center gap-2 bg-[#1A1F2E] border border-white/8 rounded-full px-3 py-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`${p.icon} text-sm`} style={{ color: p.color }} />
                    </span>
                    <span className="text-gray-300 text-xs" style={{ fontFamily: "'Inter', sans-serif" }}>{p.name}</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] inline-block" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
